import { useQuery } from '@tanstack/react-query'
import { useState, type CSSProperties } from 'react'
import {
  selfServiceArticleQuery,
  selfServicePageQuery,
  type SelfServiceArticle,
  type SelfServiceArticleDetail,
  type SelfServiceQuery,
} from '../api/easeedeskSelfService'
import { moduleOf, shellQuery } from '../api/shell'
import { AdmPageHead, type AdmStat } from '../softsee/adm/AdmPageHead'
import { Forbidden, isForbidden } from '../ui/Forbidden'
import { Modal } from '../ui/Modal'
import { Content } from '../ui/Shell'
import { Spinner } from '../ui/Spinner'
import { usePageStyles } from '../ui/usePageStyles'
import { SolutionFeedback } from './SolutionFeedback'
import { ArticleModal, CategoriesModal, useTakeDown } from './SelfServiceWriteModals'
import './TicketSolutions.css'

/**
 * "Self-Service" - `Self_service::index()`, macheta
 * `views/version-3/easeedesk/self_service/index.php`.
 *
 * Ecranul adminului: bara de filtre (cautare, categorie, publicat), lista, si
 * fereastra de previzualizare - "ce vede omul". Lista NU e DataTables si nu
 * era nici in portal: e un tabel randat de server, fara paginare. A ramas asa.
 *
 * SCRIERILE - "Write an article", "Edit", "Take it down" si "Categories" -
 * sunt in `SelfServiceWriteModals.tsx` (`Self_service::save`, `remove`,
 * `save_category`), si butoanele lor se deseneaza numai cui are dreptul de
 * scris (`canWrite`) si numai cand tabelele exista, ca in macheta veche.
 * Corpul se scrie intr-o casuta de text, nu in CKEditor - v4 nu are editorul.
 *
 * PREVIZUALIZAREA nu e un moft. Articolul se scrie intr-un editor, cu un camp
 * de minute langa el, si nicaieri in formular nu apare fraza pe care o citeste
 * omul - "Estimated fix: 10 minutes". Cine scrie nu are de unde sti ca a promis
 * ceva pana nu vede promisiunea.
 *
 * DE CE PARE ECRANUL GRI, SI CE S-A FACUT
 * ---------------------------------------
 * Fundalul nu e gresit: e #f4f5fa, spalarea pe care `adm.css` o deseneaza pe
 * `#content` pentru toate ecranele modulului, aceeasi masurata si pe ecranul
 * vechi. Gri pare pagina care nu pune NIMIC peste el: titlul, propozitia,
 * filtrele si lista stateau toate direct pe spalare, fara un singur cartonas
 * alb, si atunci ochiul nu mai vede o pagina, ci o foaie gri cu text pe ea.
 *
 * Leacul e cel de pe fisa unui element de CMDB, din aceeasi zi si din aceeasi
 * foaie: chromul modulului. `adm-page-head` - cartonasul alb cu iconita,
 * numele ecranului si cifrele din dreapta - si `adm-card` in jurul filtrelor
 * si al listei. Spalarea ramane exact ce a fost gandita sa fie: fundal in
 * spatele cartonaselor, nu pagina insasi.
 */

const STYLES = ['css/adm.css', 'css/self_service.css']


/*
 * Cele doua randuri de deasupra filtrelor.
 *
 * `app.css` stinge marginea oricarui `<p>` (`h1..h6, p, span, ul, li, a, ol
 * { margin: 0 }`) si lasa litera la marimea corpului - 10px, masurat. Asa,
 * cele doua propozitii ieseau lipite una de alta si de eticheta primului
 * filtru, la o marime la care nu le citeste nimeni. 12px e marimea pe care
 * `adm-page-note` o da randului de sub numele ecranului, deci a doua voce a
 * paginii ramane una singura.
 */
const INTRO: CSSProperties = { fontSize: 12, lineHeight: 1.5, marginBottom: 6 }
/* Ultimul rand se desparte de bara de filtre, care nu are margine deasupra. */
const INTRO_BLOCK: CSSProperties = { marginBottom: 10 }

export function SelfServicePage() {
  const shell = useQuery(shellQuery(moduleOf(typeof window === 'undefined' ? '' : window.location.pathname)))
  usePageStyles(STYLES, shell.data?.version ?? '')

  /* Bara de filtre se trimite la APASARE, ca formularul `GET` din macheta -
     nu la fiecare tasta. Ce e in casute pana atunci e `draft`. */
  const [asked, setAsked] = useState<SelfServiceQuery>({ q: '', category: 0, published: '' })
  const [draft, setDraft] = useState<SelfServiceQuery>(asked)
  const [preview, setPreview] = useState(0)
  /* Ferestrele de scris: `null` inchis, `'new'` articol nou, altfel cel de editat. */
  const [editing, setEditing] = useState<'new' | SelfServiceArticle | null>(null)
  const [categoriesOpen, setCategoriesOpen] = useState(false)
  const takeDown = useTakeDown()

  const page = useQuery(selfServicePageQuery(asked))
  const canWrite = !!(page.data?.canWrite && page.data.storage)

  if (isForbidden(page.error)) return <Forbidden />

  return (
    <Content>
      <div className="selfServiceScreen">
        <AdmPageHead
          tone="blue-hoki"
          icon="fa-book-open"
          title="Self-Service articles"
          note="what a person reads instead of opening a ticket"
          stats={countOf(page.data?.articles)}
          actions={
            /* Legatura catre jurnal, aici si nu intr-un meniu separat:
               intrebarea "ce sa scriu mai departe" si-o pune chiar cine se
               uita la lista de articole, si raspunsul ei e in jurnal. */
            <>
              <a className="btn btn-default btn-sm" href="/app/self_service/searches">
                <i className="fas fa-search"></i> What people search
              </a>
              {canWrite && (
                <>
                  {' '}
                  <button type="button" className="btn btn-default btn-sm" id="ssCategories" onClick={() => setCategoriesOpen(true)}>
                    <i className="fas fa-tags"></i> Categories
                  </button>{' '}
                  <button type="button" className="btn btn-success btn-sm" id="ssAdd" onClick={() => setEditing('new')}>
                    <i className="fas fa-plus-circle"></i> Write an article
                  </button>
                </>
              )}
            </>
          }
        />

        {page.data && !page.data.storage && (
          <div className="alert alert-warning">
            <i className="fas fa-database"></i> The tables these articles live in do not exist yet, so nothing can be
            written here. Run the <code>create_it_solutions_articles_tables</code> migration (migrations project).
          </div>
        )}

        {/*
          Cartonasul alb al modulului, in jurul a TOT ce citeste omul aici -
          propozitia, filtrele si lista. Fara el, cele trei stateau direct pe
          spalarea gri a lui `#content`, iar ecranul se citea ca o foaie gri.
          Aceeasi clasa (`adm-card`, adm.css) poarta si cartonasele de pe fisa
          unui element de CMDB.
        */}
        <div className="adm-card">
          <div style={INTRO_BLOCK}>
            {/*
             * De ce scrie asta pe ecran, si nu doar in documentatie: cine deschide
             * ecranul intai vede doua liste de ajutor in aceeasi aplicatie - asta
             * si IT Solutions - si trebuie sa ghiceasca in care sa scrie. Ghicit
             * gresit, articolul ajunge in fata cititorului nepotrivit si nimeni nu
             * afla de ce nu-l citeste nimeni.
             */}
            <p className="text-muted" style={INTRO}>
              These are for the person with the problem, not for the service desk. A runbook with escalation levels belongs
              in <strong>IT Solutions</strong>; what goes here is one thing somebody can do alone, and how long it takes.
            </p>

          </div>

          <form
            className="adm-bar"
            onSubmit={(e) => {
              e.preventDefault()
              setAsked(draft)
            }}
          >
            <div className="adm-field adm-field-wide">
              <label htmlFor="ssQ">Search</label>
              <input
                type="text"
                id="ssQ"
                className="form-control"
                placeholder="Title or summary"
                value={draft.q}
                onChange={(e) => setDraft({ ...draft, q: e.target.value })}
              />
            </div>

            <div className="adm-field">
              <label htmlFor="ssCategory">Category</label>
              <select
                id="ssCategory"
                className="form-control"
                aria-label="Category"
                value={String(draft.category)}
                onChange={(e) => setDraft({ ...draft, category: Number(e.target.value) })}
              >
                <option value="0">Every category</option>
                {(page.data?.categories ?? []).map((one) => (
                  <option value={String(one.ID)} key={one.ID}>
                    {one.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="adm-field">
              <label htmlFor="ssPublished">State</label>
              {/* Trei stari, nu doua: sirul gol inseamna "si publicate si
                  nepublicate", si nu se poate face cu un numar - acolo si
                  "nepublicate" e zero. */}
              <select
                id="ssPublished"
                className="form-control"
                aria-label="State"
                value={draft.published}
                onChange={(e) => setDraft({ ...draft, published: e.target.value })}
              >
                <option value="">Published and not</option>
                <option value="1">Published only</option>
                <option value="0">Drafts only</option>
              </select>
            </div>

            <div className="adm-field adm-field-narrow">
              <label>&nbsp;</label>
              <button type="submit" className="btn btn-theme btn-sm">
                <i className="fas fa-search"></i> Filter
              </button>
            </div>
          </form>

          {page.isPending ? (
            <Spinner />
          ) : page.isError ? (
            <div className="alert alert-danger" role="alert">
              Something went wrong: {(page.error as Error).message}
            </div>
          ) : page.data.articles.length === 0 ? (
            <div className="adm-empty-inline">
              <i className="fas fa-book-open"></i>{' '}
              {page.data.storage
                ? 'Nothing written yet. Until something is, everybody opens a ticket.'
                : 'Nothing to show until the tables exist.'}
            </div>
          ) : (
            <table className="table table-hover adm-list">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Takes</th>
                  <th>Language</th>
                  <th>State</th>
                  <th className="text-right">&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {page.data.articles.map((one) => (
                  <ArticleRow one={one} onPreview={() => setPreview(one.ID)} onEdit={canWrite ? () => setEditing(one) : null} onRemove={canWrite ? () => takeDown(one) : null} key={one.ID} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <PreviewModal id={preview} onClose={() => setPreview(0)} />
      {/* Se monteaza numai deschise: inchise, tabelul lor ar sta in pagina, ascuns. */}
      {editing !== null && <ArticleModal open editing={editing === 'new' ? null : editing} categories={page.data?.categories ?? []} roles={page.data?.roles ?? []} people={page.data?.people ?? []} onClose={() => setEditing(null)} />}
      {categoriesOpen && <CategoriesModal open categories={page.data?.categories ?? []} onClose={() => setCategoriesOpen(false)} />}
    </Content>
  )
}

/**
 * Cifrele din dreapta antetului.
 *
 * Nu costa nicio interogare - se numara chiar lista adusa, filtrata cum a cerut
 * omul. Si a treia nu e o statistica, ci lungimea listei de lucruri de facut:
 * cate articole pleaca in magazin fara sa spuna cat dureaza. De-aia are tonul
 * de asteptare cand exista, si pe cel verde cand nu mai e niciunul.
 */
function countOf(articles: SelfServiceArticle[] | undefined): AdmStat[] {
  if (articles === undefined) return []

  const published = articles.filter((one) => one.published === 1).length
  const silent = articles.filter((one) => (one.self_serve_minutes ?? 0) <= 0).length

  return [
    { value: String(published), label: 'published', tone: 'green-mode' },
    { value: String(articles.length - published), label: 'drafts', tone: 'grey' },
    { value: String(silent), label: 'no time set', tone: silent > 0 ? 'yellow' : 'green-mode' },
  ]
}

function ArticleRow({ one, onPreview, onEdit, onRemove }: { one: SelfServiceArticle; onPreview(): void; onEdit: (() => void) | null; onRemove: (() => void) | null }) {
  const minutes = one.self_serve_minutes ?? 0

  return (
    <tr>
      <td>
        <strong>{one.title}</strong>
        {one.summary.trim() !== '' && <div className="adm-muted">{one.summary}</div>}
      </td>
      <td className="adm-muted">{one.category_name === null ? 'no category' : one.category_name}</td>
      <td className="adm-nowrap">
        {/* Gol nu se umple cu nimic inventat, dar nici nu se lasa o casuta
            goala: adminul trebuie sa vada ca ii lipseste ceva, altfel
            articolul pleaca in magazin fara promisiune si nimeni nu observa. */}
        {minutes > 0 ? (
          `${minutes} min`
        ) : (
          <span className="text-warning" title="The reader is told nothing about how long this takes">
            <i className="fas fa-exclamation-triangle"></i> not set
          </span>
        )}
      </td>
      <td className="adm-muted">{one.lang.toUpperCase()}</td>
      <td>
        {one.published === 1 ? (
          <span className="label label-success">published</span>
        ) : (
          <span className="label label-default">draft</span>
        )}
      </td>
      <td className="text-right adm-nowrap">
        <button type="button" className="btn btn-xs btn-default ssPreview" title="What the person sees" onClick={onPreview}>
          <i className="fas fa-eye"></i>
        </button>
        {onEdit && (
          <>
            {' '}
            <button type="button" className="btn btn-xs btn-default ssEdit" onClick={onEdit}>
              <i className="fas fa-pencil-alt"></i>
            </button>{' '}
            <button type="button" className="btn btn-xs btn-danger ssRemove" data-title={one.title} onClick={onRemove ?? undefined}>
              <i className="fas fa-trash"></i>
            </button>
          </>
        )}
      </td>
    </tr>
  )
}

/** `Self_service::preview($id)`, macheta `preview.php`. */
function PreviewModal({ id, onClose }: { id: number; onClose(): void }) {
  const article = useQuery(selfServiceArticleQuery(id))

  return (
    <Modal id="ssModal" open={id > 0} onClose={onClose} title="What the person sees">
      {article.isPending ? (
        <Spinner />
      ) : article.isError ? (
        <div className="alert alert-danger" role="alert">
          That article did not load: {(article.error as Error).message}
        </div>
      ) : (
        <>
          <PreviewBody article={article.data} />
          {/* Notele si comentariile articolului - acelasi tabel si acelasi
              panou ca la solutiile din biblioteca si de pe fila tichetului. */}
          <div style={{ marginTop: 14 }}>
            <SolutionFeedback article={article.data.ID} />
          </div>
        </>
      )}
    </Modal>
  )
}

function PreviewBody({ article }: { article: SelfServiceArticleDetail }) {
  return (
    <>
      {article.published !== 1 && (
        <div className="alert alert-warning" style={{ padding: '8px 12px' }}>
          <i className="fas fa-eye-slash"></i> This is a draft. Nobody sees it yet.
        </div>
      )}

      <div style={{ border: '1px solid #e8ecef', borderRadius: '4px', padding: '18px 20px', background: '#fff' }}>
        <h4 style={{ marginTop: 0 }}>{article.title}</h4>

        {article.summary.trim() !== '' && <p className="text-muted">{article.summary}</p>}

        {article.minutes_label !== '' ? (
          <p>
            <span className="label label-success">
              <i className="far fa-clock"></i> {article.minutes_label}
            </span>
          </p>
        ) : (
          <p className="text-warning">
            <i className="fas fa-exclamation-triangle"></i> No time is set, so the person is told nothing about how long
            this takes.
          </p>
        )}

        <hr />

        {/*
         * CORPUL SE RANDEAZA CA HTML, SI ASTA E O HOTARARE MOSTENITA, NU O
         * SCAPARE.
         *
         * E scris in CKEditor de un operator al portalului - `<p>`, `<ul>`,
         * `<strong>` - iar `Self_service::save()` sare peste filtrul XSS chiar
         * pe campul asta, cu explicatia scrisa acolo: trecut prin `xss_clean`,
         * formatarea iese ciopartita si adminul vede cum i se strica articolul
         * la salvare. Deci in baza sta HTML de incredere, si tot asa il randa
         * `preview.php`. Poarta nu e noua: o deschid aceiasi oameni care scriu
         * de ani de zile politici si solutii in acelasi editor.
         *
         * TOT CE E TEXT - titlul, rezumatul, numele categoriilor, cautarile din
         * jurnal - trece prin React ca text, si React le scapa singur.
         */}
        <div className="ss-body" dangerouslySetInnerHTML={{ __html: article.body }} />
      </div>
    </>
  )
}
